import { Button, Icon } from "@qw/ui";
import { type ReactNode, useMemo } from "react";

import { resolveAssistantProgressPresentation } from "../assistant-progress-state";
import type { SuggestionInsertSlot } from "../chat.types";
import { useChat } from "../providers/chat-provider";
import { ChatEmpty } from "./chat-empty";
import { ChatSessionMessages } from "./chat-session-messages";
import { ErrorChatSessionMessage } from "./error-chat-session-message";
import { ThinkingChatSessionMessage } from "./thinking-chat-session-message";

/**
 * Id of the seeded welcome turn injected before a conversation has any real messages. It is
 * dropped from the transcript the moment a real user/assistant message exists.
 */
const SEEDED_WELCOME_MESSAGE_ID = "assistant-message-1";

export interface ChatContentProps {
  emptyComponent?: ReactNode;
  suggestionInsert?: SuggestionInsertSlot;
}

export const ChatContent = ({ emptyComponent, suggestionInsert }: ChatContentProps = {}) => {
  const { contentRef, messages, error, status, historyStatus, retryHistory, elicitationSubmitPhase, chatSession } =
    useChat();

  const isWaitingForResponse = status === "submitted" || status === "streaming";

  // Drop the seeded welcome turn once the conversation has any real message. The predicate is
  // whole-list, not per-message, so it is resolved ONCE per render rather than re-scanned inside the
  // filter — that nested scan was quadratic in transcript length and ran on every streamed token.
  const filteredMessages = useMemo(() => {
    const hasRealMessages = messages.some(
      (message) => message.id !== SEEDED_WELCOME_MESSAGE_ID && (message.role === "user" || message.role === "assistant")
    );

    if (!hasRealMessages) {
      return messages;
    }

    return messages.filter((message) => message.id !== SEEDED_WELCOME_MESSAGE_ID);
  }, [messages]);

  const assistantProgress = resolveAssistantProgressPresentation({
    messages: filteredMessages,
    status,
    elicitationSubmitPhase,
    sessionStatus: chatSession?.status,
  });

  // Not-ready handling (brief 015): with server-authoritative render the only path, an empty transcript
  // could mean "loading the whole-tree query", "query failed", "unsupported runtime", or "genuinely
  // empty". Show the empty welcome / loading spinner ONLY when there are no messages (otherwise a cold
  // load would flash the welcome, and a spinner would fight a live turn). The history error/unsupported
  // states, by contrast, render as a banner ABOVE the transcript even when a live turn is present —
  // otherwise a cold `conversationMessages` failure during a stream re-attach would silently hide the
  // fact that the persisted history never loaded (leaving no retry). `error` is retryable; `unsupported`
  // is a permanent capability seam, so it is non-retryable and distinct.
  const hasNoMessages = !filteredMessages?.length;
  const showEmptyWelcome = hasNoMessages && historyStatus === "ready" && !isWaitingForResponse;
  const showHistoryLoading = hasNoMessages && historyStatus === "loading" && !isWaitingForResponse;

  return (
    <div ref={contentRef} className="flex-1 min-w-0 flex flex-col p-4 *:first:-mt-2.5 *:last:-mb-2.5">
      {historyStatus === "error" && (
        <section className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-2">
            <Icon icon="cloud_off" />
            Couldn't load this conversation.
          </span>
          <Button variant="outline" size="sm" onClick={retryHistory}>
            Retry
          </Button>
        </section>
      )}

      {historyStatus === "unsupported" && (
        <section className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <Icon icon="info" />
          This conversation isn't available here.
        </section>
      )}

      {showEmptyWelcome && (emptyComponent ?? <ChatEmpty />)}

      {showHistoryLoading && (
        <section className="flex flex-auto items-center justify-center p-8 text-muted-foreground">
          <Icon icon="progress_activity" className="animate-spin text-2xl" />
        </section>
      )}

      <ChatSessionMessages
        messages={filteredMessages}
        suggestionInsert={suggestionInsert}
        assistantProgress={assistantProgress.kind === "message-footer" ? assistantProgress : undefined}
      />

      {assistantProgress.kind === "standalone" && <ThinkingChatSessionMessage label={assistantProgress.label} />}

      {error && <ErrorChatSessionMessage error={error} />}
    </div>
  );
};
